import { assetIds, replaceAssets } from 'features/report-files/public/assetReferences';
import { FrameFeedback, LivePatch, Page } from './types';

/** What the frame's document was built from, apart from the page markup: registered visuals and datasets. A change here rebuilds the frame. */
export const structureKey = (page: Page): string => JSON.stringify([page.id,
  page.visuals.map(({ id, title, dataIds, html, css, script }) => ({ id, title, dataIds, html, css, script })), page.data.map(d => d.id)]);
/** Everything the frame's document is built from. Equal keys are the same document. */
export const pageSourceKey = (page: Page): string => structureKey(page) + page.html;

export type FrameAssets = { ready: boolean; urls: Record<string, string>; errors: FrameFeedback['errors'] };

/** The rendered document can take the current assets as they are: no asset it shows changed (new ones may be added by patches). */
export const assetsCompatible = (shown: FrameAssets, current: FrameAssets): boolean =>
  Object.keys(shown.urls).every(id => shown.urls[id] === current.urls[id]) && JSON.stringify(shown.errors) === JSON.stringify(current.errors);

/** Asset references in a patch become the session's data URLs, as in the document the frame was built from; null when one is unavailable. */
export function resolvePatches(patches: LivePatch[], urls: Record<string, string>): LivePatch[] | null {
  const resolve = (value: string): string | null => assetIds(value).some(id => !urls[id]) ? null : replaceAssets(value, urls);
  const resolved: LivePatch[] = [];
  for (const patch of patches) {
    if (patch.op === 'attrs') {
      const startTag = resolve(patch.startTag);
      if (startTag === null) return null;
      resolved.push({ ...patch, startTag });
    } else if (patch.op === 'text') {
      const text = resolve(patch.text);
      if (text === null) return null;
      resolved.push({ ...patch, text });
    } else {
      const html = resolve(patch.html);
      if (html === null) return null;
      resolved.push({ ...patch, html });
    }
  }
  return resolved;
}
